import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Form, Row, Col, Table, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faTimes, faCheck, faCloudUpload, faFileAlt, faExclamationTriangle, faSave, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import { toast } from 'react-hot-toast';
import '../styles/CreateBOLWizard.css';
import moment from 'moment';

const CreateBOLWizard = ({ show, onHide, onSuccess, bolId }) => {
  // State for wizard navigation
  const [step, setStep] = useState(1);
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // State name to abbreviation mapping
  const stateAbbreviations = {
    'alabama': 'AL',
    'alaska': 'AK',
    'arizona': 'AZ',
    'arkansas': 'AR',
    'california': 'CA',
    'colorado': 'CO',
    'connecticut': 'CT',
    'delaware': 'DE',
    'florida': 'FL',
    'georgia': 'GA',
    'hawaii': 'HI',
    'idaho': 'ID',
    'illinois': 'IL',
    'indiana': 'IN',
    'iowa': 'IA',
    'kansas': 'KS',
    'kentucky': 'KY',
    'louisiana': 'LA',
    'maine': 'ME',
    'maryland': 'MD',
    'massachusetts': 'MA',
    'michigan': 'MI',
    'minnesota': 'MN',
    'mississippi': 'MS',
    'missouri': 'MO',
    'montana': 'MT',
    'nebraska': 'NE',
    'nevada': 'NV',
    'new hampshire': 'NH',
    'new jersey': 'NJ',
    'new mexico': 'NM',
    'new york': 'NY',
    'north carolina': 'NC',
    'north dakota': 'ND',
    'ohio': 'OH',
    'oklahoma': 'OK',
    'oregon': 'OR',
    'pennsylvania': 'PA',
    'rhode island': 'RI',
    'south carolina': 'SC',
    'south dakota': 'SD',
    'tennessee': 'TN',
    'texas': 'TX',
    'utah': 'UT',
    'vermont': 'VT',
    'virginia': 'VA',
    'washington': 'WA',
    'west virginia': 'WV',
    'wisconsin': 'WI',
    'wyoming': 'WY',
    'district of columbia': 'DC'
  };
  
  // Convert state name to abbreviation
  const getStateAbbreviation = (stateName) => {
    if (!stateName) return '';
    
    // If it's already a 2-letter code, return it
    if (stateName.length === 2 && /^[A-Za-z]{2}$/.test(stateName)) {
      return stateName.toUpperCase();
    }
    
    // Try to convert full name to abbreviation
    const abbreviation = stateAbbreviations[stateName.toLowerCase()];
    return abbreviation || stateName.substring(0, 2).toUpperCase(); // Fallback to first 2 letters if not found
  };
  
  // Initial form state - moved to a constant for reuse
  const initialFormState = {
    origin: {
      date: '',
      name: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    },
    destination: {
      date: '',
      name: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    },
    freightItems: [],
    currentFreightItem: {
      customerRef: '',
      mawb: '',
      hawb: '',
      description: '',
      length: '',
      width: '',
      height: '',
      pieces: '',
      weight: '',
      weightUnit: 'lbs',
      dimensionUnit: 'in'
    },
    instructions: '',
    pocName: '',
    podEmail: '',
    documents: []
  };
  
  // State for wizard navigation
  const [formData, setFormData] = useState(initialFormState);
  
  // Track document upload status
  const [uploadingDocuments, setUploadingDocuments] = useState([]);
  
  // Add new state for date validation
  const [dateErrors, setDateErrors] = useState({
    origin: '',
    destination: ''
  });
  
  // Get today's date in YYYY-MM-DD format for min attribute
  const today = moment().format('YYYY-MM-DD');
  
  // Add new state for rate information
  const [rateInfo, setRateInfo] = useState(null);
  const [calculatingRate, setCalculatingRate] = useState(false);
  
  // Add accessorial rates data at the top with other constants
  const accessorialRates = [
    { service: 'Liftgate Pickup', rate: 75.00 },
    { service: 'Liftgate Delivery', rate: 75.00 },
    { service: 'Inside Pickup', rate: 85.00 },
    { service: 'Inside Delivery', rate: 85.00 },
    { service: 'Residential Pickup', rate: 75.00 },
    { service: 'Residential Delivery', rate: 75.00 },
    { service: 'Limited Access Pickup', rate: 75.00 },
    { service: 'Limited Access Delivery', rate: 75.00 },
    { service: 'Notification/Appointment', rate: 35.00 },
    { service: 'Sort and Segregate', rate: 1.50, unit: 'per piece' },
    { service: 'Detention', rate: 35.00, unit: 'per 15 minutes' },
    { service: 'Storage', rate: 25.00, unit: 'per day' }
  ];
  
  // Load address book on component mount
  useEffect(() => {
    if (show) {
      loadAddresses();
    }
  }, [show]);
  
  const loadAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/bols/addresses`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAddresses(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to load addresses:', error);
      toast.error('Failed to load address book');
      setIsLoading(false);
    }
  };
  
  // Reset form when modal is shown
  useEffect(() => {
    if (show) {
      console.log('Resetting BOL wizard form');
      setStep(1);
      setFormData(initialFormState);
      setUploadingDocuments([]);
    }
  }, [show]);
  
  // Handle state field change with validation
  const handleStateChange = (e, type) => {
    const { value } = e.target;
    
    // Store raw input in state
    if (type === 'origin') {
      setFormData(prev => ({
        ...prev,
        origin: {
          ...prev.origin,
          state: value.toUpperCase()
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        destination: {
          ...prev.destination,
          state: value.toUpperCase()
        }
      }));
    }
  };
  
  // Validate if state is valid (2 letters)
  const isStateValid = (state) => {
    return state && state.length === 2 && /^[A-Za-z]{2}$/.test(state);
  };
  
  // Handle form field changes
  const handleOriginChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      origin: {
        ...prev.origin,
        [name]: value
      }
    }));
  };
  
  const handleDestinationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      destination: {
        ...prev.destination,
        [name]: value
      }
    }));
  };
  
  const handleFreightItemChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      currentFreightItem: {
        ...prev.currentFreightItem,
        [name]: value
      }
    }));
  };
  
  const handleInstructionsChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle address book selection
  const handleAddressSelect = (addressId, type) => {
    if (addressId === '0') return;
    
    const selectedAddress = addresses.find(addr => addr.aid === parseInt(addressId));
    if (!selectedAddress) return;
    
    // Convert state name to 2-letter code
    const stateAbbreviation = getStateAbbreviation(selectedAddress.State || '');
    
    if (type === 'origin') {
      setFormData(prev => ({
        ...prev,
        origin: {
          ...prev.origin,
          name: selectedAddress.Name || '',
          address: selectedAddress.Address || '',
          city: selectedAddress.City || '',
          state: stateAbbreviation,
          zip: selectedAddress.Zip || ''
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        destination: {
          ...prev.destination,
          name: selectedAddress.Name || '',
          address: selectedAddress.Address || '',
          city: selectedAddress.City || '',
          state: stateAbbreviation,
          zip: selectedAddress.Zip || ''
        }
      }));
    }
  };
  
  // Add freight item to the list
  const addFreightItem = () => {
    const { currentFreightItem } = formData;
    
    // Validate required fields
    if (!currentFreightItem.pieces || !currentFreightItem.weight) {
      toast.error('Pieces and Weight are required');
      return;
    }
    
    // Create a copy of the item for the list
    const itemToAdd = {
      ...currentFreightItem,
      freightDesc: currentFreightItem.description // Map description to freightDesc when adding
    };
    
    // If weight unit is kg, convert to lbs for the database
    if (itemToAdd.weightUnit === 'kg') {
      // 1 kg = 2.20462 lbs - store direct input after unit conversion
      itemToAdd.weight = (parseFloat(itemToAdd.weight) * 2.20462).toFixed(2);
      
      // Keep the original unit in the UI but note the conversion
      itemToAdd.originalWeight = currentFreightItem.weight;
      itemToAdd.originalUnit = 'kg';
    }

    // If dimension unit is cm, convert to inches for the database
    if (itemToAdd.dimensionUnit === 'cm') {
      // 1 inch = 2.54 cm, round up to ensure adequate space
      if (itemToAdd.length) {
        itemToAdd.originalLength = itemToAdd.length;
        itemToAdd.length = Math.ceil(parseFloat(itemToAdd.length) / 2.54).toString();
      }
      if (itemToAdd.width) {
        itemToAdd.originalWidth = itemToAdd.width;
        itemToAdd.width = Math.ceil(parseFloat(itemToAdd.width) / 2.54).toString();
      }
      if (itemToAdd.height) {
        itemToAdd.originalHeight = itemToAdd.height;
        itemToAdd.height = Math.ceil(parseFloat(itemToAdd.height) / 2.54).toString();
      }
      itemToAdd.originalDimensionUnit = 'cm';
      itemToAdd.dimensionUnit = 'in'; // Ensure the stored unit is inches
    }
    
    setFormData(prev => ({
      ...prev,
      freightItems: [...prev.freightItems, itemToAdd],
      currentFreightItem: {
        customerRef: '',
        mawb: '',
        hawb: '',
        description: '',
        length: '',
        width: '',
        height: '',
        pieces: '',
        weight: '',
        weightUnit: 'lbs',
        dimensionUnit: 'in'
      }
    }));
    
    toast.success('Freight item added');
  };
  
  // Remove freight item from the list
  const removeFreightItem = (index) => {
    setFormData(prev => ({
      ...prev,
      freightItems: prev.freightItems.filter((_, i) => i !== index)
    }));
    
    toast.success('Freight item removed');
  };
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Check file size (max 5MB)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`Some files exceed the 5MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    // Filter out the oversized files
    const filesToUpload = files.filter(file => file.size <= 5 * 1024 * 1024);
    
    // Add files to uploading state first
    setUploadingDocuments(prev => [
      ...prev,
      ...filesToUpload.map(file => ({
        name: file.name,
        size: file.size,
        status: 'uploading'
      }))
    ]);
    
    // Upload files sequentially to avoid race conditions
    for (const file of filesToUpload) {
      const formData = new FormData();
      formData.append('file', file, file.name); // Explicitly add filename
      
      try {
        // First verify the session is active
        try {
          const debugResponse = await fetch(`${API_URL}/api/bols/debug-session`, { 
            credentials: 'include' 
          });
          if (!debugResponse.ok) {
            throw new Error('Session verification failed');
          }
        } catch (sessionError) {
          console.error('Session verification failed:', sessionError);
          throw new Error('Session expired - please refresh the page');
        }

        // Attempt the upload
        console.log('Uploading file:', {
          name: file.name,
          size: file.size,
          type: file.type
        });

        const response = await fetch(`${API_URL}/api/bols/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const responseData = await response.json();

        console.log('Upload response:', responseData);

        // Verify the response contains the expected data
        if (!responseData || !responseData.fileName || !responseData.url) {
          throw new Error('Invalid response from server');
        }
        
        // Update uploading status
        setUploadingDocuments(prev => 
          prev.filter(doc => doc.name !== file.name)
        );
        
        // Add uploaded document metadata to state
        setFormData(prev => ({
          ...prev,
          documents: [
            ...prev.documents,
            {
              name: file.name,
              size: file.size,
              type: file.type,
              blobName: responseData.fileName,
              blobUrl: responseData.url,
              uploadSuccess: true
            }
          ]
        }));
        
        console.log('Document added to state:', {
          name: file.name,
          size: file.size,
          type: file.type,
          blobName: responseData.fileName,
          blobUrl: responseData.url
        });

        toast.success(`${file.name} uploaded successfully`);
      } catch (err) {
        console.error('Upload failed:', err);
        
        // Update uploading status to error
        setUploadingDocuments(prev => 
          prev.map(doc => 
            doc.name === file.name 
              ? { 
                  ...doc, 
                  status: 'error', 
                  error: err.message || 'Upload failed'
                }
              : doc
          )
        );
        
        toast.error(`Failed to upload ${file.name}: ${err.message || 'Upload failed'}`);
        
        // Remove failed upload after 5 seconds
        setTimeout(() => {
          setUploadingDocuments(prev => 
            prev.filter(doc => doc.name !== file.name)
          );
        }, 5000);
      }
    }
  };
  
  // Remove document from the list
  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };
  
  // Submit the form
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      
      // Check if there are any uploads in progress
      if (uploadingDocuments.length > 0) {
        toast.error('Please wait for all document uploads to complete');
        setIsLoading(false);
        return;
      }
      
      // Create FormData for BOL creation
      const formDataToSend = new FormData();
      formDataToSend.append('origin', JSON.stringify(formData.origin));
      formDataToSend.append('destination', JSON.stringify(formData.destination));
      
      // Clean up freight items before sending to backend
      const cleanedFreightItems = formData.freightItems.map(item => {
        const { weightUnit, originalWeight, originalUnit, description, ...cleanItem } = item;
        return {
          ...cleanItem,
          freightDesc: description
        };
      });
      formDataToSend.append('freightItems', JSON.stringify(cleanedFreightItems));
      formDataToSend.append('instructions', formData.instructions || '');
      formDataToSend.append('pocName', formData.pocName || '');
      formDataToSend.append('podEmail', formData.podEmail || '');
      
      // Filter out any documents that were not successfully uploaded
      const successfulDocuments = formData.documents.filter(doc => doc.uploadSuccess && doc.blobUrl && doc.blobName);
      
      console.log('Current documents in state:', formData.documents);
      console.log('Successful documents filtered:', successfulDocuments);
      
      // Add document metadata
      if (successfulDocuments.length > 0) {
        // Only send necessary document information
        const documentMetadata = successfulDocuments.map(doc => ({
          name: doc.name,
          blobName: doc.blobName,
          blobUrl: doc.blobUrl,
          size: doc.size,
          type: doc.type
        }));
        console.log('Sending document metadata in BOL creation:', documentMetadata);
        formDataToSend.append('documentMetadata', JSON.stringify(documentMetadata));
      }
      
      // First try to verify the session is active
      try {
        const debugResponse = await fetch(`${API_URL}/api/bols/debug-session`, { 
          credentials: 'include' 
        });
        if (!debugResponse.ok) {
          throw new Error('Session verification failed');
        }
        console.log('Session is active, proceeding with BOL creation');
      } catch (sessionError) {
        console.error('Session verification failed:', sessionError);
        toast.error('Session expired - please refresh the page and try again');
        setIsLoading(false);
        return;
      }
      
      console.log('Submitting BOL with session credentials and form data:', {
        hasOrigin: !!formDataToSend.get('origin'),
        hasDestination: !!formDataToSend.get('destination'),
        hasFreightItems: !!formDataToSend.get('freightItems'),
        hasDocumentMetadata: !!formDataToSend.get('documentMetadata'),
        documentMetadataContent: formDataToSend.get('documentMetadata')
      });

      // Submit to API with explicit credentials options
      const response = await fetch(`${API_URL}/api/bols/create`, {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      
      console.log('BOL creation response:', responseData);
      
      if (responseData.documentResults) {
        const failedDocs = responseData.documentResults.filter(doc => !doc.success);
        if (failedDocs.length > 0) {
          console.error('Some documents failed to process:', failedDocs);
          toast.error(`Warning: ${failedDocs.length} document(s) failed to process`);
        }
      }
      
      toast.success('BOL created successfully');
      setIsLoading(false);
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(responseData.bolId);
      }
      onHide();
    } catch (error) {
      console.error('Failed to create BOL:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Session error - try refreshing the page';
      toast.error(`Failed to create BOL: ${errorMsg}`);
      setIsLoading(false);
    }
  };
  
  // Navigate between steps
  const nextStep = () => {
    // Validate current step
    if (step === 1) {
      // Validate location step
      const { origin, destination } = formData;
      
      // Create arrays to track missing fields
      const missingOriginFields = [];
      const missingDestinationFields = [];
      const invalidFields = [];
      
      // Check required origin fields
      if (!origin.date) missingOriginFields.push('Date');
      if (!origin.name) missingOriginFields.push('Name');
      if (!origin.address) missingOriginFields.push('Address');
      if (!origin.city) missingOriginFields.push('City');
      if (!origin.state) missingOriginFields.push('State');
      if (!origin.zip) missingOriginFields.push('Zip');
      
      // Check required destination fields
      if (!destination.date) missingDestinationFields.push('Date');
      if (!destination.name) missingDestinationFields.push('Name');
      if (!destination.address) missingDestinationFields.push('Address');
      if (!destination.city) missingDestinationFields.push('City');
      if (!destination.state) missingDestinationFields.push('State');
      if (!destination.zip) missingDestinationFields.push('Zip');
      
      // Validate state codes
      if (origin.state && origin.state.length !== 2) {
        invalidFields.push('Origin State must be a 2-letter code');
      }
      
      if (destination.state && destination.state.length !== 2) {
        invalidFields.push('Destination State must be a 2-letter code');
      }
      
      // Show validation errors
      if (invalidFields.length > 0) {
        toast.error(invalidFields.join(', '));
        return;
      }
      
      // Show appropriate error messages for missing fields
      if (missingOriginFields.length > 0 && missingDestinationFields.length > 0) {
        toast.error(`Please fill in all required fields for both Origin and Destination`);
        return;
      } else if (missingOriginFields.length > 0) {
        toast.error(`Please fill in these Origin fields: ${missingOriginFields.join(', ')}`);
        return;
      } else if (missingDestinationFields.length > 0) {
        toast.error(`Please fill in these Destination fields: ${missingDestinationFields.join(', ')}`);
        return;
      }
      
      // Validate date formats
      if (new Date(origin.date) > new Date(destination.date)) {
        toast.error('Pickup date cannot be after delivery date');
        return;
      }
    } else if (step === 2) {
      // Validate freight step
      if (formData.freightItems.length === 0) {
        toast.error('Please add at least one freight item');
        return;
      }
    }
    
    setStep(prev => Math.min(prev + 1, 4));
  };
  
  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };
  
  // Reset form when closing
  const handleClose = () => {
    setStep(1);
    setFormData(initialFormState);
    setUploadingDocuments([]);
    onHide();
  };
  
  // Add function to save address to address book
  const saveToAddressBook = async (type) => {
    try {
      const addressData = type === 'origin' ? formData.origin : formData.destination;
      
      // Validate required fields
      if (!addressData.name || !addressData.address || !addressData.city || !addressData.state || !addressData.zip) {
        toast.error('Please fill in all address fields before saving');
        return;
      }

          const response = await fetch(`${API_URL}/api/bols/addresses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        name: addressData.name,
        address: addressData.address,
        city: addressData.city,
        state: addressData.state,
        zip: addressData.zip
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = await response.json();

      if (responseData) {
        // Refresh address book
        await loadAddresses();
        toast.success('Address saved to address book');
      }
    } catch (error) {
      console.error('Failed to save address:', error);
      toast.error('Failed to save address to address book');
    }
  };

  // Function to check if address is in address book
  const isAddressInBook = (type) => {
    const addressData = type === 'origin' ? formData.origin : formData.destination;
    
    // Only check if we have all required fields
    if (!addressData.name || !addressData.address || !addressData.city || !addressData.state || !addressData.zip) {
      return true; // Return true to hide save icon if fields are incomplete
    }

    return addresses.some(addr => 
      addr.Name?.toUpperCase() === addressData.name?.toUpperCase() &&
      addr.Address?.toUpperCase() === addressData.address?.toUpperCase() &&
      addr.City?.toUpperCase() === addressData.city?.toUpperCase() &&
      addr.State?.toUpperCase() === addressData.state?.toUpperCase() &&
      addr.Zip === addressData.zip
    );
  };
  
  // Handle origin date change with validation
  const handleOriginDateChange = (e) => {
    const newDate = e.target.value;
    
    // Clear previous errors
    setDateErrors(prev => ({ ...prev, origin: '' }));
    
    // Update the form data
    setFormData(prev => ({
      ...prev,
      origin: {
        ...prev.origin,
        date: newDate
      }
    }));
    
    // Validate destination date if it exists
    if (formData.destination.date) {
      if (moment(formData.destination.date).isBefore(moment(newDate))) {
        setDateErrors(prev => ({
          ...prev,
          destination: 'Delivery date cannot be before pickup date'
        }));
      } else {
        setDateErrors(prev => ({ ...prev, destination: '' }));
      }
    }
  };

  // Handle destination date change with validation
  const handleDestinationDateChange = (e) => {
    const newDate = e.target.value;
    
    // Clear previous errors
    setDateErrors(prev => ({ ...prev, destination: '' }));
    
    // Validate against origin date
    if (formData.origin.date && moment(newDate).isBefore(moment(formData.origin.date))) {
      setDateErrors(prev => ({
        ...prev,
        destination: 'Delivery date cannot be before pickup date'
      }));
    }
    
    // Update the form data
    setFormData(prev => ({
      ...prev,
      destination: {
        ...prev.destination,
        date: newDate
      }
    }));
  };
  
  // Add function to calculate rate
  const calculateRate = async () => {
    try {
      setCalculatingRate(true);
      const response = await fetch(`${API_URL}/api/bols/calculate-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          origin: {
            city: formData.origin.city,
            zip: formData.origin.zip
          },
          destination: {
            city: formData.destination.city,
            zip: formData.destination.zip
          },
          freightItems: formData.freightItems
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success) {
        setRateInfo(responseData);
      } else {
        console.warn('Rate calculation warning:', responseData.message);
        setRateInfo({ error: responseData.message });
      }
    } catch (error) {
      console.error('Error calculating rate:', error);
      setRateInfo({ error: 'Failed to calculate rate' });
      toast.error('Failed to calculate estimated rate');
    } finally {
      setCalculatingRate(false);
    }
  };

  // Call calculateRate when reaching the confirmation step
  useEffect(() => {
    if (step === 4 && formData.freightItems.length > 0) {
      calculateRate();
    }
  }, [step]);
  
  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Create New BOL Request</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="wizard-container">
          {/* Wizard Navigation */}
          <div className="wizard-navigation mb-4">
            <ul className="nav nav-pills nav-wizard">
              <li className={`nav-item ${step === 1 ? 'active' : ''}`}>
                <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setStep(1); }}>
                  <div className="wizard-step-number">1</div>
                  <div className="wizard-step-label">Location</div>
                </a>
              </li>
              <li className={`nav-item ${step === 2 ? 'active' : ''}`}>
                <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setStep(2); }}>
                  <div className="wizard-step-number">2</div>
                  <div className="wizard-step-label">Freight</div>
                </a>
              </li>
              <li className={`nav-item ${step === 3 ? 'active' : ''}`}>
                <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setStep(3); }}>
                  <div className="wizard-step-number">3</div>
                  <div className="wizard-step-label">Instructions</div>
                </a>
              </li>
              <li className={`nav-item ${step === 4 ? 'active' : ''}`}>
                <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); setStep(4); }}>
                  <div className="wizard-step-number">4</div>
                  <div className="wizard-step-label">Confirmation</div>
                </a>
              </li>
            </ul>
          </div>
          
          {/* Step 1: Location */}
          {step === 1 && (
            <div className="step-content">
              <h4 className="mb-2">Origin and Destination</h4>
              <p className="text-muted small mb-3">Fields marked with <span className="text-danger">*</span> are required.</p>
              <Row>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h5 className="mb-0">Origin</h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="date" 
                              name="date" 
                              value={formData.origin.date}
                              onChange={handleOriginDateChange}
                              min={today}
                              isInvalid={!!dateErrors.origin}
                              required
                            />
                            <Form.Control.Feedback type="invalid">
                              {dateErrors.origin}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          <Form.Group className="mb-3">
                            <Form.Label>Address Book</Form.Label>
                            <Form.Select 
                              name="addressBook"
                              onChange={(e) => handleAddressSelect(e.target.value, 'origin')}
                            >
                              <option value="0">Select from address book</option>
                              {addresses.map(addr => (
                                <option key={addr.aid} value={addr.aid}>
                                  {addr.Name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <InputGroup>
                          <Form.Control 
                            type="text" 
                            name="name" 
                            value={formData.origin.name}
                            onChange={handleOriginChange}
                            required
                          />
                          {!isAddressInBook('origin') && (
                            <Button 
                              variant="outline-secondary"
                              onClick={() => saveToAddressBook('origin')}
                              title="Save to address book"
                            >
                              <FontAwesomeIcon icon={faSave} />
                            </Button>
                          )}
                        </InputGroup>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          type="text" 
                          name="address" 
                          value={formData.origin.address}
                          onChange={handleOriginChange}
                          required
                        />
                      </Form.Group>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>City <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="text" 
                              name="city" 
                              value={formData.origin.city}
                              onChange={handleOriginChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group className="mb-3">
                            <Form.Label>State <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="text" 
                              name="state" 
                              value={formData.origin.state}
                              onChange={(e) => {
                                handleOriginChange(e);
                                handleStateChange(e, 'origin');
                              }}
                              maxLength={2}
                              placeholder="2-letter code"
                              pattern="[A-Za-z]{2}"
                              isInvalid={formData.origin.state && !isStateValid(formData.origin.state)}
                              required
                              className="text-uppercase"
                            />
                            {formData.origin.state && !isStateValid(formData.origin.state) && (
                              <Form.Control.Feedback type="invalid">
                                State must be a 2-letter code
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Zip <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="text" 
                              name="zip" 
                              value={formData.origin.zip}
                              onChange={handleOriginChange}
                              maxLength={5}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h5 className="mb-0">Destination</h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="date" 
                              name="date" 
                              value={formData.destination.date}
                              onChange={handleDestinationDateChange}
                              min={formData.origin.date || today}
                              isInvalid={!!dateErrors.destination}
                              required
                            />
                            <Form.Control.Feedback type="invalid">
                              {dateErrors.destination}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          <Form.Group className="mb-3">
                            <Form.Label>Address Book</Form.Label>
                            <Form.Select 
                              name="addressBook"
                              onChange={(e) => handleAddressSelect(e.target.value, 'destination')}
                            >
                              <option value="0">Select from address book</option>
                              {addresses.map(addr => (
                                <option key={addr.aid} value={addr.aid}>
                                  {addr.Name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <InputGroup>
                          <Form.Control 
                            type="text" 
                            name="name" 
                            value={formData.destination.name}
                            onChange={handleDestinationChange}
                            required
                          />
                          {!isAddressInBook('destination') && (
                            <Button 
                              variant="outline-secondary"
                              onClick={() => saveToAddressBook('destination')}
                              title="Save to address book"
                            >
                              <FontAwesomeIcon icon={faSave} />
                            </Button>
                          )}
                        </InputGroup>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          type="text" 
                          name="address" 
                          value={formData.destination.address}
                          onChange={handleDestinationChange}
                          required
                        />
                      </Form.Group>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>City <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="text" 
                              name="city" 
                              value={formData.destination.city}
                              onChange={handleDestinationChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group className="mb-3">
                            <Form.Label>State <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="text" 
                              name="state" 
                              value={formData.destination.state}
                              onChange={(e) => {
                                handleDestinationChange(e);
                                handleStateChange(e, 'destination');
                              }}
                              maxLength={2}
                              placeholder="2-letter code"
                              pattern="[A-Za-z]{2}"
                              isInvalid={formData.destination.state && !isStateValid(formData.destination.state)}
                              required
                              className="text-uppercase"
                            />
                            {formData.destination.state && !isStateValid(formData.destination.state) && (
                              <Form.Control.Feedback type="invalid">
                                State must be a 2-letter code
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Zip <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                              type="text" 
                              name="zip" 
                              value={formData.destination.zip}
                              onChange={handleDestinationChange}
                              maxLength={5}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
          
          {/* Step 2: Freight */}
          {step === 2 && (
            <div className="step-content">
              <h4 className="mb-4">What type of freight do you have?</h4>
              <Row>
                <Col md={12}>
                  <Card className="mb-4">
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Customer Ref</Form.Label>
                            <Form.Control 
                              type="text" 
                              name="customerRef" 
                              value={formData.currentFreightItem.customerRef}
                              onChange={handleFreightItemChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>MAWB</Form.Label>
                            <Form.Control 
                              type="text" 
                              name="mawb" 
                              value={formData.currentFreightItem.mawb}
                              onChange={handleFreightItemChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>HAWB</Form.Label>
                            <Form.Control 
                              type="text" 
                              name="hawb" 
                              value={formData.currentFreightItem.hawb}
                              onChange={handleFreightItemChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control 
                              type="text" 
                              name="description" 
                              value={formData.currentFreightItem.description}
                              onChange={handleFreightItemChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={1}>
                          <Form.Group className="mb-3">
                            <Form.Label>Length</Form.Label>
                            <Form.Control 
                              type="number" 
                              name="length" 
                              value={formData.currentFreightItem.length}
                              onChange={handleFreightItemChange}
                              min="0"
                              style={{ width: '70px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={1}>
                          <Form.Group className="mb-3">
                            <Form.Label>Width</Form.Label>
                            <Form.Control 
                              type="number" 
                              name="width" 
                              value={formData.currentFreightItem.width}
                              onChange={handleFreightItemChange}
                              min="0"
                              style={{ width: '70px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={1}>
                          <Form.Group className="mb-3">
                            <Form.Label>Height</Form.Label>
                            <Form.Control 
                              type="number" 
                              name="height" 
                              value={formData.currentFreightItem.height}
                              onChange={handleFreightItemChange}
                              min="0"
                              style={{ width: '70px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end mb-3">
                          <Form.Group>
                            <Form.Check
                              inline
                              type="radio"
                              label="in"
                              name="dimensionUnit"
                              id="dimensionUnitIn"
                              value="in"
                              checked={formData.currentFreightItem.dimensionUnit === 'in'}
                              onChange={handleFreightItemChange}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              label="cm"
                              name="dimensionUnit"
                              id="dimensionUnitCm"
                              value="cm"
                              checked={formData.currentFreightItem.dimensionUnit === 'cm'}
                              onChange={handleFreightItemChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={1}>
                          <Form.Group className="mb-3">
                            <Form.Label>Pieces</Form.Label>
                            <Form.Control 
                              type="number" 
                              name="pieces" 
                              value={formData.currentFreightItem.pieces}
                              onChange={handleFreightItemChange}
                              min="1"
                              required
                              style={{ width: '70px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={1}>
                          <Form.Group className="mb-3">
                            <Form.Label>Weight</Form.Label>
                            <Form.Control 
                              type="number" 
                              name="weight" 
                              value={formData.currentFreightItem.weight}
                              onChange={handleFreightItemChange}
                              min="0"
                              required
                              style={{ width: '70px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end mb-3">
                          <Form.Group>
                            <Form.Check
                              inline
                              type="radio"
                              label="lbs"
                              name="weightUnit"
                              id="weightUnitLbs"
                              value="lbs"
                              checked={formData.currentFreightItem.weightUnit === 'lbs'}
                              onChange={handleFreightItemChange}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              label="kg"
                              name="weightUnit"
                              id="weightUnitKg"
                              value="kg"
                              checked={formData.currentFreightItem.weightUnit === 'kg'}
                              onChange={handleFreightItemChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Button 
                            variant="primary" 
                            onClick={addFreightItem}
                            disabled={!formData.currentFreightItem.pieces || !formData.currentFreightItem.weight}
                          >
                            Add Freight
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  
                  <Card className="mb-4">
                    <Card.Header>
                      <h5 className="mb-0">Freight Items</h5>
                    </Card.Header>
                    <Card.Body>
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Customer Ref</th>
                            <th>MAWB</th>
                            <th>HAWB</th>
                            <th>Description</th>
                            <th>Dimensions</th>
                            <th>Pieces</th>
                            <th>Weight</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.freightItems.length > 0 ? (
                            formData.freightItems.map((item, index) => (
                              <tr key={index}>
                                <td>{item.customerRef}</td>
                                <td>{item.mawb}</td>
                                <td>{item.hawb}</td>
                                <td>{item.freightDesc}</td>
                                <td>
                                  {item.length && item.width && item.height ? (
                                    item.originalDimensionUnit === 'cm' ?
                                      `${item.originalLength}x${item.originalWidth}x${item.originalHeight} cm (${item.length}x${item.width}x${item.height} in)` :
                                      `${item.length}x${item.width}x${item.height} in`
                                  ) : ''}
                                </td>
                                <td>{item.pieces}</td>
                                <td>
                                  {item.originalWeight 
                                    ? `${item.originalWeight} ${item.originalUnit} (${item.weight} lbs)` 
                                    : `${item.weight} ${item.weightUnit}`}
                                </td>
                                <td>
                                  <Button 
                                    variant="danger" 
                                    size="sm"
                                    onClick={() => removeFreightItem(index)}
                                  >
                                    Remove
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center">No freight items added</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
          
          {/* Step 3: Instructions & Documents */}
          {step === 3 && (
            <div className="step-content">
              <h4 className="mb-4">Special handling or documents?</h4>
              <Row>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h5 className="mb-0">Upload Documents</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="document-upload-area mb-3">
                        <div className="upload-container">
                          <label htmlFor="document-upload" className="upload-label">
                            <FontAwesomeIcon icon={faCloudUpload} size="3x" className="mb-2" />
                            <div>Drag and drop files here or click to select</div>
                            <div className="text-muted small">Max 5MB per file</div>
                          </label>
                          <input
                            id="document-upload"
                            type="file"
                            className="document-input"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            multiple
                            onChange={handleFileUpload}
                          />
                        </div>
                      </div>
                      
                      <div className="document-list">
                        {formData.documents.length > 0 || uploadingDocuments.length > 0 ? (
                          <>
                            {formData.documents.map((doc, index) => (
                              <div key={`doc-${index}`} className="document-item">
                                <div className="document-info">
                                  <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                  <span>{doc.name}</span>
                                  <span className="text-muted ms-2">
                                    ({Math.round(doc.size / 1024)} KB)
                                  </span>
                                </div>
                                <Button
                                  variant="link"
                                  className="p-0 text-danger"
                                  onClick={() => removeDocument(index)}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </Button>
                              </div>
                            ))}
                            
                            {uploadingDocuments.map((doc, index) => (
                              <div key={`uploading-${index}`} className="document-item">
                                <div className="document-info">
                                  {doc.status === 'uploading' ? (
                                    <Spinner animation="border" size="sm" className="me-2" />
                                  ) : (
                                    <FontAwesomeIcon 
                                      icon={faExclamationTriangle} 
                                      className="me-2 text-danger" 
                                    />
                                  )}
                                  <span>{doc.name}</span>
                                  <span className="text-muted ms-2">
                                    ({Math.round(doc.size / 1024)} KB)
                                  </span>
                                  {doc.status === 'uploading' && (
                                    <span className="ms-2 text-primary">Uploading...</span>
                                  )}
                                  {doc.status === 'error' && (
                                    <span className="ms-2 text-danger">{doc.error || 'Upload failed'}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="text-center text-muted">No documents uploaded</div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h5 className="mb-0">Special Instructions</h5>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Instructions (Include HAZMAT or Liftgate etc.)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          name="instructions"
                          value={formData.instructions}
                          onChange={handleInstructionsChange}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Your Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="pocName"
                          value={formData.pocName}
                          onChange={handleInstructionsChange}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>POD Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="podEmail"
                          value={formData.podEmail}
                          onChange={handleInstructionsChange}
                        />
                        <Form.Text className="text-muted">
                          Enter email address to receive delivery confirmation
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
          
          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="step-content">
              <h4 className="mb-4">Confirmation</h4>
              <Row className="justify-content-center">
                <Col sm={10}>
                  <Card className="mb-4">
                    <Card.Body>
                      <Row className="mb-3">
                        <Col sm={1} className="text-center">
                          <FontAwesomeIcon icon={faCheck} size="lg" className="text-success" />
                        </Col>
                        <Col sm={11}>
                          <strong>Location Information:</strong> From {formData.origin.name} to {formData.destination.name}
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col sm={1} className="text-center">
                          <FontAwesomeIcon icon={faCheck} size="lg" className="text-success" />
                        </Col>
                        <Col sm={11}>
                          <strong>Freight Items:</strong> {formData.freightItems.length} item(s) added
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col sm={1} className="text-center">
                          <FontAwesomeIcon icon={faCheck} size="lg" className="text-success" />
                        </Col>
                        <Col sm={11}>
                          <strong>Documents:</strong> {formData.documents.length} document(s) attached
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col sm={1} className="text-center">
                          <FontAwesomeIcon icon={faCheck} size="lg" className="text-success" />
                        </Col>
                        <Col sm={11}>
                          <strong>Special Instructions:</strong> {formData.instructions ? 'Added' : 'None'}
                        </Col>
                      </Row>
                      
                      {/* Add Rate Information Section */}
                      <Row className="mt-4">
                        <Col>
                          <Card className="bg-light">
                            <Card.Body>
                              <h5 className="mb-3">
                                <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                                Estimated Rate
                              </h5>
                              {calculatingRate ? (
                                <div className="text-center py-3">
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Calculating rate...
                                </div>
                              ) : rateInfo ? (
                                rateInfo.error ? (
                                  <Alert variant="warning">
                                    {rateInfo.error}
                                  </Alert>
                                ) : (
                                  <Table borderless size="sm" className="mb-0">
                                    <tbody>
                                      <tr>
                                        <td>Base Rate:</td>
                                        <td className="text-end">${rateInfo.rate.toFixed(2)}</td>
                                      </tr>
                                      <tr>
                                        <td>Fuel Surcharge:</td>
                                        <td className="text-end">${rateInfo.fsc.toFixed(2)}</td>
                                      </tr>
                                      <tr className="fw-bold">
                                        <td>Estimated Total:</td>
                                        <td className="text-end">${rateInfo.total.toFixed(2)}</td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                )
                              ) : null}
                              <div className="text-muted small mt-2">
                                Note: This is an estimated rate and may vary based on actual shipping conditions.
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      {/* Add Accessorial Rates Section after the estimated rate card */}
                      <Row className="mt-4">
                        <Col>
                          <Card>
                            <Card.Header>
                              <h5 className="mb-0">Available Accessorial Services</h5>
                            </Card.Header>
                            <Card.Body>
                              <Table striped bordered hover responsive>
                                <thead>
                                  <tr>
                                    <th>Service</th>
                                    <th className="text-end">Rate</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {accessorialRates.map((item, index) => (
                                    <tr key={index}>
                                      <td>{item.service}</td>
                                      <td className="text-end">
                                        ${item.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        {item.unit && ` ${item.unit}`}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                              <small className="text-muted">
                                * Additional charges may apply based on specific requirements and circumstances.
                                Please contact customer service for detailed information about accessorial services.
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  <Alert variant="info">
                    Please review all information before submitting. Once submitted, a new BOL will be created and our team will be notified.
                  </Alert>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        {step > 1 && (
          <Button variant="outline-primary" onClick={prevStep}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Previous
          </Button>
        )}
        {step < 4 ? (
          <Button variant="primary" onClick={nextStep}>
            Next
            <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
          </Button>
        ) : (
          <Button 
            variant="success" 
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Submit BOL Request'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateBOLWizard; 